import './Partners.scss';
import Table from '../../components/Table/Table';
import { createColumnHelper } from '@tanstack/react-table';
import magnifier from 'assets/images/icons/magnifier.svg';
import plus from 'assets/images/icons/plus.svg';
import eyeIcon from 'assets/images/icons/eye.svg';
import Input from '../../components/Input/Input';
import { Button } from '../../components/Button/Button';
import { Link, useNavigate } from 'react-router-dom';
import { Partner } from 'services/partner/partner.types';
import usePartnersVM from './usePartnersVM';
import { Collapsible } from 'components/Collapsible/Collapsible';
import { Formik } from 'formik';
import { Modal } from 'components/Modal/Modal';
import { CreatePartnerCategorySchema } from './PartnerFormsSchema';
import { getDatalists } from '../../features/datalist/datalist.slice';
import { useSelector } from 'react-redux';
import trashIcon from 'assets/images/icons/trashcan.svg';
import { FaFilter } from 'react-icons/fa';
import { MultiSelect } from 'components/MultiSelect/MultiSelect';
import { ContentBlock } from 'components/ContentBlock/ContentBlock';
export type PartnersPageProps = {};

export const Partners: React.FunctionComponent = () => {
    const navigate = useNavigate();

    const {
        partners,
        search,
        partnersFiltered,
        handleSearch,
        handleNewPartnerCategory,
        handleEditPartnerCategory,
        isPartnerCategoryModalOpen,
        setIsPartnerCategoryModalOpen,
        partnerCategoryModalMode,
        setPartnerCategoryModalMode,
        partnerCategoryToEdit,
        setPartnerCategoryToEdit,
        partnerCategoryToDelete,
        setPartnerCategoryToDelete,
        handleModalTitle,
        handleModalYesText,
        handleDeletePartnerCategory,
        isPartnerModalOpen,
        setIsPartnerModalOpen,
        partnerToDelete,
        setPartnerToDelete,
        handleDeletePartner,
        exportPartners,
        setFilterIsOpen,
        filter,
        setFilter,
        filterIsOpen
    } = usePartnersVM();

    const datalistData = useSelector(getDatalists);

    const columnHelper = createColumnHelper<Partner>();

    const tableColumns = [
        columnHelper.accessor('name', {
            header: 'Nom complet',
            cell: (info) => <i>{info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('target', {
            header: 'Public concerné',
            cell: (info) => {
                const targetKey = info.getValue();
                const displayValue =
                    targetKey && datalistData.datalists?.partner_targets?.[targetKey]
                        ? datalistData.datalists.partner_targets[targetKey]
                        : targetKey;
                return <i>{displayValue}</i>;
            },
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('fullname', {
            header: 'Interlocuteur',
            cell: (info) => <i>{info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('role', {
            header: 'Fonction',
            cell: (info) => <i>{info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('phone', {
            header: 'Téléphone',
            cell: (info) => <i>{info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.accessor('email', {
            header: 'Email',
            cell: (info) => <i>{info.getValue()}</i>,
            footer: (info) => info.column.id
        }),
        columnHelper.display({
            header: 'Voir / Supprimer',
            cell: ({ row: { original: currRow } }) => {
                return (
                    <div style={{ display: 'flex', columnGap: 12 }}>
                        <span>
                            <Link to={`/app/admin/partners/${currRow.id}`}>
                                <img src={eyeIcon} alt="eye-icon" />
                            </Link>
                        </span>
                        <span style={{ display: 'flex', columnGap: 12 }}>
                            <img
                                src={trashIcon}
                                alt="trash-icon"
                                onClick={() => {
                                    setPartnerToDelete(currRow);
                                    setIsPartnerModalOpen(true);
                                }}
                            />
                        </span>
                    </div>
                );
            }
        })
    ];

    return (
        <>
            <main id="partners">
                <div className="top">
                    <div className="search-container">
                        <Input
                            placeholder="Rechercher un partenaire"
                            name="search-person"
                            className="search-person search-bar"
                            icon={<img src={magnifier} alt="" />}
                            onChange={handleSearch}
                            value={search ? search : ''}
                        />
                        <Button
                            variant="light"
                            className="filter-button"
                            icon={<FaFilter />}
                            iconBefore
                            chip={filter.departments.length + filter.targets.length}
                            callback={() => setFilterIsOpen(!filterIsOpen)}
                        />
                    </div>
                    <div className="actions-buttons">
                        <Button
                            label="Exporter les partenaires"
                            variant="light"
                            callback={exportPartners}
                        />
                        <Button
                            label="Ajouter une catégorie"
                            variant="light"
                            icon={<img src={plus} alt="" />}
                            iconBefore
                            callback={() => setIsPartnerCategoryModalOpen(true)}
                        />
                        <Button
                            label="Ajouter un partenaire"
                            variant="light"
                            icon={<img src={plus} alt="" />}
                            iconBefore
                            callback={() => navigate('/app/admin/partners/new')}
                        />
                    </div>
                </div>
                {filterIsOpen && (
                    <ContentBlock title="Filtrer par">
                        <div className="filter-container">
                            <div className="form">
                                <div className="form-line">
                                    <div className="form-group">
                                        <label>Département(s)</label>
                                        <MultiSelect
                                            name="departments"
                                            placeholder="Selectionner des départements"
                                            values={filter.departments}
                                            labelKey="label"
                                            valueKey="value"
                                            update={(e: { label: string; value: string }[]) => {
                                                setFilter({
                                                    ...filter,
                                                    departments: e
                                                });
                                            }}
                                            options={
                                                Object.entries(datalistData.datalists?.departments ?? {}).map(([key, val]) => ({
                                                    label: val,
                                                    value: key
                                                }))
                                            }
                                            colors={[]}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Public concerné(s)</label>
                                        <MultiSelect
                                            name="targets"
                                            placeholder="Selectionner des publics concernés"
                                            values={filter.targets}
                                            labelKey="label"
                                            valueKey="value"
                                            update={(e: { label: string; value: string }[]) => {
                                                setFilter({
                                                    ...filter,
                                                    targets: e
                                                });
                                            }}
                                            options={
                                                Object.entries(datalistData.datalists?.partner_targets ?? {}).map(([key, val]) => ({
                                                    label: val,
                                                    value: key
                                                }))
                                            }
                                            colors={[]}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </ContentBlock>
                )}
                {partnersFiltered?.map((category, index) => (
                    <Collapsible
                        key={index}
                        title={category.label}
                        isOpen={index === 0}
                        showActions
                        canEdit={category.id !== 0}
                        className={category.id === 0 ? 'uncategorized' : ''}
                        canDelete={category.id !== 0}
                        onEdit={() => {
                            setPartnerCategoryToEdit({
                                id: category.id,
                                label: category.label
                            });
                            setPartnerCategoryModalMode('edit');
                            setIsPartnerCategoryModalOpen(true);
                        }}
                        onDelete={() => {
                            setPartnerCategoryToDelete({
                                id: category.id,
                                label: category.label
                            });
                            setPartnerCategoryModalMode('delete');
                            setIsPartnerCategoryModalOpen(true);
                        }}
                    >
                        {category.partners.length > 0 ? (
                            category.partners.map((department, index) => (
                                <Collapsible
                                    key={index}
                                    title={`${department.department_label} (${department.department_code})`}
                                    isOpen={index === 0}
                                    isInside
                                >
                                    <Table
                                        data={department.data}
                                        columns={tableColumns}
                                        columnHelper={columnHelper}
                                        className="table-card"
                                    />
                                </Collapsible>
                            ))
                        ) : (
                            <div>Aucun partenaire trouvé</div>
                        )}
                    </Collapsible>
                ))}

            </main>
            <Modal
                open={isPartnerCategoryModalOpen}
                setClose={() => setIsPartnerCategoryModalOpen(false)}
                title={handleModalTitle()}
                yesText={handleModalYesText()}
                noText="Annuler"
                onClose={() => {
                    setPartnerCategoryToDelete(undefined);
                    setPartnerCategoryToEdit(undefined);
                    setPartnerCategoryModalMode('create');
                    setIsPartnerCategoryModalOpen(false);
                }}
                formId="PartnerCategoryForm"
                onValidate={() => {
                    if (partnerCategoryModalMode === 'delete') {
                        handleDeletePartnerCategory();
                    } else {
                        document
                            .getElementById('PartnerCategoryForm')
                            ?.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                    }
                }}
            >
                {partnerCategoryModalMode === 'delete' ? (
                    <div>
                        <p>Voulez-vous vraiment supprimer la catégorie {partnerCategoryToDelete?.label} ?</p>
                    </div>
                ) : (
                    <Formik
                        initialValues={{
                            label: partnerCategoryToEdit?.label || ''
                        }}
                        enableReinitialize={true}
                        validationSchema={CreatePartnerCategorySchema}
                        onSubmit={async (values, { setSubmitting }) => {
                            if (partnerCategoryModalMode === 'create') {
                                handleNewPartnerCategory(values);
                            } else {
                                handleEditPartnerCategory(values);
                            }
                            setSubmitting(false);
                        }}
                    >
                        {(props) => (
                            <form id="PartnerCategoryForm" onSubmit={props.handleSubmit} noValidate>
                                <div className="form">
                                    <div className="form-line">
                                        <div className="form-group">
                                            <label>Nom de la catégorie</label>
                                            <input
                                                type="text"
                                                name="label"
                                                placeholder="Nom de la catégorie"
                                                onChange={props.handleChange}
                                                value={props.values.label}
                                            />
                                            {props.errors.label && props.touched.label && (
                                                <p className="error">{String(props.errors.label)}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </form>
                        )}
                    </Formik>
                )}
            </Modal>
            <Modal
                open={isPartnerModalOpen}
                setClose={() => setIsPartnerModalOpen(false)}
                title="Supprimer un partenaire"
                yesText="Supprimer"
                noText="Annuler"
                onClose={() => {
                    setIsPartnerModalOpen(false);
                    setPartnerToDelete(undefined);
                }}
                onValidate={() => {
                    handleDeletePartner();
                }}
            >
                <div>
                    <p>Voulez-vous vraiment supprimer le partenaire {partnerToDelete?.name || ''} ?</p>
                </div>
            </Modal>
        </>
    );
};
